import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/outline';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import * as yup from 'yup';
import { AddressInputGroup } from '~/components/form/AddressInputGroup';
import { SubmitButton } from '~/components/form/SubmitButton';
import { TextField } from '~/components/form/TextField';
import { SelectField } from '~/components/form/downshift/SelectField';
import { SingleUploadField, UploadResult } from '~/components/upload2/Upload';
import { ContactType, Status } from '~/generated/graphql';
import { graphql } from '~/gql';
import {
  labelToRelationships,
  uniqueSorted,
} from '~/helpers/contactTypeMappings';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { SideLayout } from '~/layouts/side/SideLayout';

// Internal Formik values type
export type FormValues = {
  status: Status;
  type: ContactType;
  name: string;
  organisationId: string[];
  types: string[];
  streetAddress: unknown;
  postalAddress: unknown;
  email: string;
  phone: string;
  images: UploadResult[]; // Formik uses UploadResult[]
  notes: string;
};

// Type for the values passed to the onSubmit prop (output of the form)
export type ShortContactOutputFormValues = Omit<FormValues, 'images'> & {
  images: string[]; // onSubmit prop receives string[] for images
  relationship?: string[];
};

type Props = {
  type?: 'Customer' | 'Assignee' | 'Owner';
  defaultName?: string;
  onSubmit: (values: ShortContactOutputFormValues) => void; // Updated prop type
  emailIsRequired?: boolean;
  isLoading?: boolean;
};

function createSchema(emailIsRequired: boolean) {
  const email = yup.string().email();

  return yup.object().shape({
    name: yup.string().required(),
    email: emailIsRequired ? email.required() : email,
  });
}

const CONTACT_TYPE_MAPPINGS_Q = graphql(`
  query ContactTypeMappingsForShortForm {
    contactTypeMappings {
      label
      relationships
    }
  }
`);

// Align ShortContactForm options with ContactForm by using the same sources
const CONTACT_TYPE_CONFIG_Q = graphql(`
  query ContactTypeConfig_ForForm {
    contactTypeConfig {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
      updatedAt
    }
  }
`);
const CONTACT_TYPES_PRESET_Q = graphql(`
  query ContactTypesPreset_ForForm($preset: Boolean) {
    contactTypes(preset: $preset)
  }
`);
const CONTACT_TYPES_IN_USE_Q = graphql(`
  query ContactTypesInUse_ForForm {
    contactTypesInUse
  }
`);

export const ShortContactForm = ({
  type,
  defaultName,
  onSubmit,
  emailIsRequired = false,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const [{ data: mappingsData }] = useQuery({ query: CONTACT_TYPE_MAPPINGS_Q });
  const [{ data: cfgData, error: cfgError }] = useQuery({
    query: CONTACT_TYPE_CONFIG_Q,
    requestPolicy: 'cache-and-network',
  });
  const [{ data: presetData }] = useQuery({
    query: CONTACT_TYPES_PRESET_Q,
    variables: { preset: true },
  });
  const [{ data: inUseData, error: inUseError }] = useQuery({
    query: CONTACT_TYPES_IN_USE_Q,
  });

  // Log non-blocking errors for debugging but don't block the form
  // These queries may fail due to permissions, but the form can work with preset types only
  if (inUseError) {
    console.warn(
      'ShortContactForm: contactTypesInUse query failed, continuing with preset types only',
      inUseError
    );
  }
  if (cfgError) {
    console.warn(
      'ShortContactForm: contactTypeConfig query failed (likely permissions), continuing with preset types only',
      cfgError
    );
  }
  const mappings: { label: string; relationships: string[] }[] = useMemo(
    () => mappingsData?.contactTypeMappings ?? [],
    [mappingsData?.contactTypeMappings]
  );

  // Build base relationship list to mirror ContactForm ordering and filtering
  const serverTypes: string[] = useMemo(
    () => cfgData?.contactTypeConfig?.types ?? [],
    [cfgData]
  );
  const relPreset: string[] = useMemo(
    () => presetData?.contactTypes ?? [],
    [presetData]
  );
  const relInUse: string[] = useMemo(
    () => (inUseData?.contactTypesInUse ?? []).filter((v: string) => !!v),
    [inUseData]
  );
  const knownRelationships = useMemo(
    () => uniqueSorted([...(relPreset || []), ...(relInUse || [])]),
    [relPreset, relInUse]
  );
  const baseRelationships = useMemo(() => {
    if (serverTypes.length) {
      const extrasInUse = knownRelationships.filter(
        (t) => !serverTypes.includes(t)
      );
      return [...serverTypes, ...uniqueSorted(extrasInUse)];
    }
    return knownRelationships;
  }, [serverTypes, knownRelationships]);

  const typeOptions = useMemo(() => {
    // relationship -> label lookup from mappings
    const relToLabel = new Map<string, string>();
    const labelSet = new Set(mappings.map((m) => m.label));
    for (const m of mappings) {
      for (const r of m.relationships || []) {
        const key = String(r);
        const preferred = labelSet.has(key) ? key : m.label;
        // Identity label wins; otherwise set only if unseen
        if (!relToLabel.has(key) || preferred === key) {
          relToLabel.set(key, preferred);
        }
      }
    }
    const translate = (label: string) => {
      const key = `contactRelationships.${label}`;
      const translated = t(key);
      return translated === key ? label : translated;
    };
    const labelsOrdered: string[] = [];
    const seen = new Set<string>();
    for (const rel of baseRelationships) {
      const label = relToLabel.get(String(rel)) ?? String(rel);
      if (!label || seen.has(label)) continue;
      seen.add(label);
      labelsOrdered.push(label);
    }
    return labelsOrdered.map((label) => ({
      value: label,
      label: translate(label),
    }));
  }, [mappings, baseRelationships, t]);

  const initialValues: FormValues = {
    status: Status.Active,
    type: ContactType.Person,
    name: defaultName ?? '',
    organisationId: [],
    types: type === 'Customer' ? ['Customer'] : [],
    streetAddress: null,
    postalAddress: null,
    email: '',
    phone: '',
    images: [],
    notes: '',
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        const relationship = labelToRelationships(values.types, mappings);
        const transformedValues = {
          ...values,
          images: values.images.length > 0 ? [values.images[0].id] : [],
          relationship,
        };
        onSubmit(transformedValues as ShortContactOutputFormValues);
      }}
      validationSchema={createSchema(emailIsRequired)}
    >
      <SideLayout isPopup>
        <SideLayout.Body>
          <br />
          <TextField
            className='mb-5 pt-2'
            name='name'
            label={t('name')}
            autoFocus={!isMobile}
            required
          />
          <TextField
            name='email'
            label={t('emailAddress')}
            type='email'
            required
          />
          <TextField name='phone' label={t('phoneNumber')} type='phone' />
          <Disclosure>
            {({ open }) => (
              <>
                <Disclosure.Button className='flex pb-4 pt-6 text-left text-xs text-brand underline'>
                  <ChevronUpIcon
                    className={`${
                      !open ? 'rotate-180 transform pt-0.5' : ''
                    } mr-1 h-5 w-5  `}
                  />
                  {open ? <p>Less Details</p> : <p>More Details</p>}
                </Disclosure.Button>
                <Disclosure.Panel className='pb-2 text-sm text-copy-body'>
                  <SelectField
                    name='types'
                    label={t('Type')}
                    options={typeOptions}
                    multiple
                    showAllOption={false}
                  />

                  <AddressInputGroup
                    name='streetAddress'
                    label={t('streetAddress')}
                  />
                  <AddressInputGroup
                    name='postalAddress'
                    label={t('postalAddress')}
                  />
                  <TextField name='notes' label={t('notes')} type='textarea' />
                  <SingleUploadField
                    imagesOnly
                    name='images'
                    id='contact-image'
                    isMainUpload
                  />
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </SideLayout.Body>
        <SideLayout.Foot className='p-4'>
          <SubmitButton loading={isLoading} />
        </SideLayout.Foot>
      </SideLayout>
    </Formik>
  );
};

// Removed static TypePicker in favor of dynamic options built from mappings
